import { TRIP_STATUS_MAP } from '@returnmates/client-core/src/constants/trip'
import {
  Notification,
  Trip,
  TripCxlReasonType,
  TripFailureReasonType,
  TripStatus,
  TripSuccessReasonType,
} from '@returnmates/client-core/src/graphql/generated/api'
import AutocompleteInput from '@returnmates/ui-core/src/components/AutocompleteInput'
import OpenLinkIcon from '@returnmates/ui-core/src/components/imageComponents/flyoutSidebar/OpenLinkIcon'
import { WILSON_URL } from '@returnmates/ui-core/src/constants/paths'
import { HTMLAttributes, memo, useCallback, useMemo } from 'react'
import { Field, useField } from 'react-final-form'

import Select from '../../../components/Select'
import CellIcon from '../../CellIcon'
import EditAddressFormInner from '../../EditAddressForm/EditAddressFormInner'
import ExpandableSection from '../../ExpandableSection'
import RescheduledIcon from '../../images/icons/rescheduledIcon'
import PreviousCustomerInformation from '../../PreviousCustomerInformation'
import {
  enabledStatusesObj,
  FAILURE_REASONS,
  SUCCESS_REASONS,
  TRIP_CANCELLATION_DISABLED_REASONS,
  TRIP_CANCELLATION_REASONS,
} from '../constants'
import useStyles from '../styles'
import Ids from './Ids'
import Information from './Information'
import Media from './Media'
import NotificationsHistory from './NotificationsHistory'
import Packages from './Packages'
import StatusHistory from './StatusHistory'
import TripSource from './TripSource'

interface Props {
  currentTrip: Trip | null
}

function EditPickupFormInner({ currentTrip }: Props) {
  const classes = useStyles()

  const { input: statusInput } = useField<TripStatus>('status')
  const { input: failureReasonInput } = useField('failureReason')
  const { input: tripCxlReasonInput } = useField('tripCxlReason')
  const { input: successReasonInput } = useField('successReason')

  const disabledTripStatuses = useMemo(() => {
    const enabledStatuses = enabledStatusesObj[statusInput.value]

    const disabledStatuses = Object.keys(TripStatus).filter(
      (status: TripStatus) => !enabledStatuses?.includes(status),
    )

    return disabledStatuses
  }, [statusInput.value])

  const isAddressReadOnly = useMemo(
    () =>
      currentTrip?.status === TripStatus.IN_PROGRESS ||
      currentTrip?.status === TripStatus.PENDING_API,
    [currentTrip?.status],
  )

  const failureReasonOnChange = useCallback(
    (_, value) => {
      failureReasonInput.onChange(value)
    },
    [failureReasonInput],
  )

  const tripCxlReasonOnChange = useCallback(
    (_, value) => {
      tripCxlReasonInput.onChange(value)
    },
    [tripCxlReasonInput],
  )

  const isShowCancellationReason = useMemo(
    () => statusInput.value === TripStatus.CANCELED || currentTrip?.tripCxlReason,
    [statusInput.value, currentTrip?.tripCxlReason],
  )

  const successReasonOnChange = useCallback(
    (_, value) => {
      successReasonInput.onChange(value)
    },
    [successReasonInput],
  )

  const openTrip = useCallback(() => {
    if (typeof window !== 'undefined') {
      const trackingLink = `${WILSON_URL}/trips?tripId=${currentTrip?.rescheduledTripId}`
      window.open(trackingLink, '_blank')?.focus()
    }
  }, [currentTrip?.rescheduledTripId])

  return (
    <>
      <Ids currentTrip={currentTrip} />

      <div className={classes.detailsRow}>
        <p className={classes.inputHead}>Status</p>
        <div>
          <Field name="status">
            {({ input }) => (
              <Select
                value={input.value}
                onChange={input.onChange}
                values={TRIP_STATUS_MAP}
                className={classes.statusSelect}
                showItemIcon
                fullWidth
                disabledValues={disabledTripStatuses}
              />
            )}
          </Field>
          {currentTrip?.rescheduledTripId ? (
            <div className={classes.rescheduleWrapper}>
              <CellIcon iconComponent={RescheduledIcon} />
              <p className={classes.rescheduledText}>Trip Rescheduled</p>
              <div className={classes.idActions}>
                <OpenLinkIcon onClick={openTrip} />
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {statusInput.value === TripStatus.FAILED || currentTrip?.failureReason ? (
        <div className={classes.detailsRow}>
          <p className={classes.inputHead}>Failure Reason</p>
          <Field name="failureReason">
            {({ input, meta }) => (
              <div className={classes.selectWrapper}>
                <AutocompleteInput
                  options={Object.keys(FAILURE_REASONS) || []}
                  value={input.value || null}
                  onChange={failureReasonOnChange}
                  isOptionEqualToValue={(option: string, value: string) => option === value}
                  getOptionLabel={(option: string) =>
                    FAILURE_REASONS[option as TripFailureReasonType] || ''
                  }
                  renderOption={(events: HTMLAttributes<HTMLElement>, option: string) => (
                    <p className={classes.optionPrimary} {...events} key={option}>
                      {FAILURE_REASONS[option as TripFailureReasonType]}
                    </p>
                  )}
                  placeholder="Choose Trip Failure Reason"
                  meta={meta}
                  inputRootStyles={classes.inputRoot}
                />
              </div>
            )}
          </Field>
        </div>
      ) : null}

      {isShowCancellationReason && (
        <div className={classes.detailsRow}>
          <p className={classes.inputHead}>Cancellation Reason</p>
          <Field name="tripCxlReason">
            {({ input, meta }) => (
              <div className={classes.selectWrapper}>
                <AutocompleteInput
                  options={Object.keys(TRIP_CANCELLATION_REASONS) || []}
                  value={input.value || null}
                  onChange={tripCxlReasonOnChange}
                  isOptionEqualToValue={(option: string, value: string) => option === value}
                  getOptionLabel={(option: string) =>
                    TRIP_CANCELLATION_REASONS[option as TripCxlReasonType] || ''
                  }
                  renderOption={(events: HTMLAttributes<HTMLElement>, option: string) => (
                    <p className={classes.optionPrimary} {...events} key={option}>
                      {TRIP_CANCELLATION_REASONS[option as TripCxlReasonType]}
                    </p>
                  )}
                  placeholder="Choose Trip Cancellation Reason"
                  meta={meta}
                  inputRootStyles={classes.inputRoot}
                  disabledOptions={TRIP_CANCELLATION_DISABLED_REASONS}
                />
              </div>
            )}
          </Field>
        </div>
      )}

      {statusInput.value === TripStatus.COMPLETE || currentTrip?.successReason ? (
        <div className={classes.detailsRow}>
          <p className={classes.inputHead}>Success Reason</p>
          <Field name="successReason">
            {({ input, meta }) => (
              <div className={classes.selectWrapper}>
                <AutocompleteInput
                  options={Object.keys(SUCCESS_REASONS) || []}
                  value={input.value || null}
                  onChange={successReasonOnChange}
                  isOptionEqualToValue={(option: string, value: string) => option === value}
                  getOptionLabel={(option: string) =>
                    SUCCESS_REASONS[option as TripSuccessReasonType] || ''
                  }
                  renderOption={(events: HTMLAttributes<HTMLElement>, option: string) => (
                    <p className={classes.optionPrimary} {...events} key={option}>
                      {SUCCESS_REASONS[option as TripSuccessReasonType]}
                    </p>
                  )}
                  placeholder="Choose Trip Success Reason"
                  meta={meta}
                  inputRootStyles={classes.inputRoot}
                />
              </div>
            )}
          </Field>
        </div>
      ) : null}

      <ExpandableSection isExpandedByDefault title="Customer Information">
        <EditAddressFormInner
          isReadOnly={isAddressReadOnly}
          userId={currentTrip?.address?.userId}
        />
      </ExpandableSection>

      {currentTrip?.previousAddresses.length ? (
        <ExpandableSection title="Previous Customer Information">
          <PreviousCustomerInformation currentTrip={currentTrip} />
        </ExpandableSection>
      ) : null}

      <ExpandableSection title="Information">
        <Information currentTrip={currentTrip} />
      </ExpandableSection>

      {currentTrip?.packages?.length ? (
        <ExpandableSection childrenType="table" title="Packages">
          <Packages packages={currentTrip.packages} />
        </ExpandableSection>
      ) : null}

      {currentTrip?.media?.length ? (
        <ExpandableSection title="Media">
          <Media media={currentTrip?.media} />
        </ExpandableSection>
      ) : null}

      <ExpandableSection childrenType="table" title="Status History">
        <StatusHistory currentTrip={currentTrip} />
      </ExpandableSection>

      <ExpandableSection childrenType="table" title="Trip Source">
        <TripSource currentTrip={currentTrip} />
      </ExpandableSection>

      {currentTrip?.notifications?.length ? (
        <ExpandableSection childrenType="table" title="Notification History">
          <NotificationsHistory notificationReports={currentTrip.notifications as Notification[]} />
        </ExpandableSection>
      ) : null}
    </>
  )
}

export default memo(EditPickupFormInner)
